import { z } from 'zod';
import { centAmount, isoDate } from './posting.js';
/**
 * Der Vertrag über das **Ausleseergebnis** eines Eingangsbelegs — die zwei
 * unteren Sprossen der Leiter (`NORTHSTAR.md`, Nachtrag „Erst messen, dann
 * Leiter bauen", und der Nachtrag vom 2026-08-22, der sie in Bau gibt).
 *
 * **Was hier steht, ist keine Erkennung, und der Vertrag sagt das mechanisch.**
 * Die Absage des Nordsterns gilt drei Dingen: **Konfidenz**, **Review wegen
 * Unsicherheit** und **Fremddienst**. Keines davon hat hier ein Feld, und
 * `.strict()` sorgt dafür, dass auch keines nachwachsen kann: Ein Dienst, der
 * dem Ergebnis eine `confidence` beilegte, macht die Prüfung rot statt still.
 * Ein Feld ist **gefunden oder leer** — es gibt keinen dritten Zustand und
 * keinen Zahlenwert dazwischen.
 *
 * **Warum das Ergebnis je Feld seine Quelle trägt.** Bei einer hybriden
 * Rechnung — XML im PDF — ist der Vorsteuerabzug **nur aus dem strukturierten
 * Teil** möglich (`[G]` UStAE Abschnitt 15.2a Abs. 4a Satz 4 in der Fassung vom
 * 15.10.2025; belegt in `docs/scribbles/2026-08-19-eingangsrechnungen.md` 6.3).
 * Ohne die Quelle am Feld wäre nach dem Zusammenführen nicht mehr zu sehen,
 * **welche Hälfte** der Rechnung einen Wert getragen hat — und genau das ist
 * die Frage, die im Konfliktfall zählt.
 *
 * **Warum die Beträge nicht `number` sind, auch hier nicht.** Sie kommen als
 * Zeichenkette aus dem XML beziehungsweise aus der Textlage. Das ist ein
 * Geschenk (Hausregel 1): Wer sie unterwegs durch `number` schickt, hat den
 * Cent verloren, bevor ihn jemand sieht.
 */
/**
 * Woher ein ausgelesener Wert stammt.
 *
 * **Die Namen sind die der Datenbank und nicht eigene.**
 * `incoming_extraction_source` (Migration `0043`, `structured_xml` seit
 * `0057`) führt seit Stufe A `manual`, `zugferd_xml`, `text_layer` und
 * `ocr`; die drei hier geführten sind Sprosse 1 in ihren zwei Erscheinungen
 * und Sprosse 2. Ein eigenes Paar Namen — `xml` und `textlage` — setzte eine
 * Übersetzungstabelle zwischen Leser und Spalte, und eine
 * Übersetzungstabelle ist die Stelle, an der zwei Namen für dieselbe Sache
 * auseinanderlaufen.
 *
 * **`zugferd_xml` und `structured_xml` sind beide Sprosse 1** — dasselbe
 * eingebettete oder rohe Rechnungs-XML, nur mit verschiedener Herkunft.
 * `zugferd_xml` gilt dem XML-Anhang eines hybriden PDF (ZUGFeRD, Factur-X);
 * `structured_xml` gilt dem nackten, strukturierten XML ohne PDF-Hülle —
 * heute eine UBL-XRechnung, künftig auch eine CII-Rechnung als Rohdatei.
 * Zwei Werte und nicht einer, weil `zugferd_xml` sonst eine ZUGFeRD/Factur-X-
 * Datei im Archiv **behauptete**, wo in Wahrheit eine eigenständige
 * XML-Datei liegt (Migration `0057`, dort die volle Herleitung).
 *
 * **`manual` und `ocr` stehen hier nicht.** `manual` ist das Gegenteil einer
 * Auslesung, und `ocr` ist Sprosse 3 — sie ist nicht gebaut, und ein
 * Aufzählungswert für etwas, das es nicht gibt, ist ein Versprechen.
 */
export const extractionSourceSchema = z.enum(['zugferd_xml', 'structured_xml', 'text_layer']);
/** Ein ausgelesenes Feld: der Wert und woher er stammt. */
function ausgelesen(wert) {
    return z.object({ wert, quelle: extractionSourceSchema }).strict();
}
/**
 * Ein Steuersatz in Prozent, zweistellig — `19.00`, `7.00`, `0.00`.
 *
 * **Kein Geldbetrag und deshalb nicht `centAmount`.** Ein Prozentsatz hat keine
 * Währung, und `Money` durch die Steuerrechnung zu tragen hiesse, 19 Prozent
 * und 19 Euro denselben Typ zu geben. Die Form ist dieselbe wie die des
 * Vorschlags aus `prefill.ts`, damit die Maske zwei Vorbelegungen desselben
 * Feldes nicht unterschiedlich formatiert bekommt.
 */
export const taxRateSchema = z.string().regex(/^\d{1,2}\.\d{2}$/, 'Steuersatz als 19.00 erwartet');
/**
 * Ein Widerspruch: zwei Fundstellen desselben Feldes, die einander ausschliessen.
 *
 * **Er ist der Grund, warum das Feld leer bleibt, und keine Warnung neben einem
 * gefüllten Feld.** Wo zwei Regeln streiten, gewinnt keine — ein Wert, der zu
 * 50 Prozent stimmt, ist in einer Buchhaltung schlimmer als kein Wert, weil ihn
 * niemand mehr nachschlägt. Was gefunden wurde, steht trotzdem da: Der Mensch
 * an der Maske soll sehen, **woran** die Auslesung gescheitert ist, statt ein
 * leeres Feld ohne Erklärung vorzufinden.
 */
export const extractionConflictSchema = z
    .object({
    /** Der Feldname aus diesem Vertrag, etwa `taxRate`. */
    feld: z.string().min(1),
    quelle: extractionSourceSchema,
    /** Die unvereinbaren Fundstellen, wörtlich wie gelesen. Mindestens zwei. */
    gefunden: z.array(z.string()).min(2),
})
    .strict();
/**
 * Woher eine **Rechnungsposition** stammt — die Namen der Spalte
 * `eingangsrechnung_position.quelle` (Migration `0325`).
 *
 * **Eigene Namen und nicht `extractionSourceSchema`.** Dort unterscheidet die
 * Quelle den Empfangsweg des XML (`zugferd_xml` gegen `structured_xml`); bei
 * einer Position zählt nur, ob sie aus dem **strukturierten Teil** kam
 * (`xml`), aus der **Textlage** (`textlage`) oder **von Hand** (`manuell`) —
 * Letzteres ist der Zustand, sobald ein Mensch eine Zeile geändert hat.
 */
export const positionQuelleSchema = z.enum(['xml', 'textlage', 'manuell']);
/**
 * Ein Dezimalwert mit begrenzter Nachkommazahl, als Zeichenkette (Hausregel 1).
 *
 * Menge und Einzelpreis sind **keine Cent-Beträge**: Ein Einzelpreis hat auf
 * einer Rechnung gern vier Nachkommastellen (`0,0049` je Einheit), eine Menge
 * bis zu sechs. Sie als `centAmount` zu führen hiesse, sie abzuweisen; sie durch
 * `number` zu schicken, den Cent zu verlieren.
 */
function dezimal(nachkomma, vorzeichen) {
    return z
        .string()
        .regex(new RegExp(`^${vorzeichen ? '-?' : ''}\\d{1,12}(\\.\\d{1,${nachkomma}})?$`), `Dezimalzahl mit höchstens ${nachkomma} Nachkommastellen erwartet`);
}
/**
 * Eine Rechnungsposition — eine Zeile der Tabelle einer Rechnung.
 *
 * **Alles ausser der Bezeichnung und dem Zeilen-Netto darf fehlen.** Eine
 * Pauschale trägt keine Menge, ein Kassenbon keinen Steuersatz je Zeile; ein
 * erfundener Wert (`1` als Menge) wäre eine Angabe, die auf der Rechnung nicht
 * steht. **`netto` ist die Zeilensumme ohne Steuer und darf negativ sein** — eine
 * Rabattzeile ist eine Position mit negativem Netto und kein Abzug im Kopf.
 */
export const belegPositionSchema = z
    .object({
    bezeichnung: z.string().trim().min(1).max(500),
    menge: dezimal(6, true).nullable(),
    einheit: z.string().trim().min(1).max(20).nullable(),
    einzelpreisNetto: dezimal(4, true).nullable(),
    steuersatz: taxRateSchema.nullable(),
    netto: centAmount,
    quelle: positionQuelleSchema,
})
    .strict();
/**
 * Warum die Zeilen einer Rechnung **nicht** übernommen wurden — der Bericht der
 * Summenprobe.
 *
 * **Eine Erkennung, die meistens stimmt, wird nicht nachgeschlagen** — deshalb
 * gilt für Zeilen aus der Textlage alles oder nichts: Die Summe der
 * Zeilen-Netto muss auf den Cent das Netto des Kopfes ergeben, sonst bleiben die
 * Zeilen leer und dieser Bericht nennt die Differenz. Er steht neben
 * `widersprueche` und aus demselben Grund: Der Mensch an der Maske soll sehen,
 * **woran** die Lesung gescheitert ist.
 */
export const positionenBefundSchema = z
    .object({
    /**
     * - `summe_abweichend` — die Zeilen ergeben nicht das Netto des Kopfes.
     * - `steuer_abweichend` — die Steuer, aus den Zeilen je Satz gerechnet, trifft den Kopf nicht.
     * - `kein_kopfnetto` — der Kopf trägt kein Netto, gegen das sich prüfen liesse.
     * - `zeile_unlesbar` — eine Zeile der Tabelle liess sich nicht lesen.
     */
    grund: z.enum(['summe_abweichend', 'steuer_abweichend', 'kein_kopfnetto', 'zeile_unlesbar']),
    /** Zahl der erkannten Zeilen, die verworfen wurden. */
    zeilen: z.number().int().nonnegative(),
    /** Die Summe der Zeilen-Netto, soweit gerechnet. */
    zeilenSumme: centAmount.nullable(),
    /** Das Netto (beziehungsweise die Steuer) des Kopfes, gegen das geprüft wurde. */
    kopfSumme: centAmount.nullable(),
    /** `zeilenSumme - kopfSumme`, auf den Cent. */
    differenz: centAmount.nullable(),
})
    .strict();
/**
 * Das Ausleseergebnis eines Belegs.
 *
 * **Jedes Feld ist `null`, wenn nichts gefunden wurde** — und `null` heisst hier
 * genau das und nicht „unsicher". Die Maske behandelt einen ausgelesenen Wert
 * wie einen Kreditor-Erfahrungswert: **vorbelegt**, und der Mensch bestätigt
 * (`NORTHSTAR.md`, Nachtrag vom 2026-08-22).
 */
export const belegExtractionSchema = z
    .object({
    /** `BT-1` der EN 16931. Eine Kleinbetragsrechnung nach § 33 UStDV hat keine. */
    invoiceNo: ausgelesen(z.string().min(1).max(200)).nullable(),
    /** `BT-2`, das Ausstellungsdatum. */
    invoiceDate: ausgelesen(isoDate).nullable(),
    /** `BT-72`, der Tag der Leistung. */
    serviceDate: ausgelesen(isoDate).nullable(),
    /**
     * Der Leistungs**monat**, als sein Erster.
     *
     * Getrennt von `serviceDate`, weil `incoming_invoice` die zwei Spalten
     * getrennt führt: Eine Dauerleistung hat einen Monat und keinen Tag, und
     * ein erfundener Tag wäre eine Angabe, die auf der Rechnung nicht steht.
     */
    serviceMonth: ausgelesen(isoDate).nullable(),
    /** `BT-5`, dreistellig nach ISO 4217. */
    currency: ausgelesen(z.string().length(3)).nullable(),
    /** `BT-109`, die Summe ohne Steuer. */
    netTotal: ausgelesen(centAmount).nullable(),
    /** `BT-110`, der Steuerbetrag. */
    taxTotal: ausgelesen(centAmount).nullable(),
    /** `BT-112`, der Bruttobetrag. */
    grossTotal: ausgelesen(centAmount).nullable(),
    /** `BT-119`. Leer, sobald die Rechnung **mehrere** Sätze trägt. */
    taxRate: ausgelesen(taxRateSchema).nullable(),
    /** `BT-31`, die USt-IdNr. des Ausstellers. */
    vendorVatId: ausgelesen(z.string().min(3).max(30)).nullable(),
    /** `BT-9`, das Fälligkeitsdatum. Steht längst nicht auf jeder Rechnung. */
    dueDate: ausgelesen(isoDate).nullable(),
    /**
     * `BT-84`, die IBAN des Zahlungsempfängers.
     *
     * **Sie ist kein Feld der Erfassungsmaske und steht trotzdem hier.** Sie ist
     * der einzige Wert des Belegs, mit dem sich der Kreditor gegen die
     * Bankbewegung prüfen lässt — und die Auslesung ist die einzige Stelle, an
     * der sie ohne zusätzliche Arbeit anfällt. Wer sie hier wegliesse, müsste
     * das Dokument ein zweites Mal aufschlagen.
     */
    iban: ausgelesen(z.string().min(15).max(34)).nullable(),
    /** Die Felder, die wegen zweier unvereinbarer Fundstellen leer geblieben sind. */
    widersprueche: z.array(extractionConflictSchema),
    /**
     * Die Zeilen der Rechnung (`InvoiceLine` im XML, die Tabelle in der
     * Textlage). **Leer heisst: keine gelesen** — nicht «die Rechnung hat keine».
     * Aus dem XML kommen sie vollständig, aus der Textlage nur, wenn die
     * Summenprobe auf den Cent aufging (`positionenBefund`).
     */
    positionen: z.array(belegPositionSchema),
    /** Warum erkannte Zeilen der Textlage verworfen wurden — `null`, wenn nichts zu berichten ist. */
    positionenBefund: positionenBefundSchema.nullable(),
})
    .strict();
//# sourceMappingURL=beleg-auslese.js.map